import { Box, SearchField, Spinner, Table } from '@factorialco/deprecated-design-system'
import { OnePagination, useDataCollectionData } from '@factorialco/f0-react/dist/experimental'
import createComputed from 'modules/core/hooks/createComputed'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import type FiltersForm from 'modules/core/lib/forms/Filters'
import i18n from 'modules/core/lib/i18n'

import { observer } from 'mobx-react'

import getUserByItem from 'modules/payroll_bundle/components/common/helper/getUserByItem'
import { ERROR, SUCCESS } from 'modules/payroll_bundle/lib/constants'
import { disablePaymentItem } from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/_common/disableRowItem'

import {
  type DraftPaymentItemLike,
  type PaymentItemLikeMinimal,
  useDraftPaymentItemsDataCollectionSource,
} from './useDraftPaymentItemsDataCollectionSource'
import Header from './Header'
import PaymentGroups from './PaymentGroups'
import { GroupProp } from './types'

const useGroupPaymentItems = createComputed((itemsToShow: DraftPaymentItemLike[]) => {
  const errorRows = i18n.t('payroll_bundle.results_payments.payments.generate_payment.error_rows')
  const successRows = i18n.t(
    'payroll_bundle.results_payments.payments.generate_payment.success_rows'
  )

  const defaultGroupResultConceptItems: GroupProp[] = [
    { name: errorRows, count: 0, status: ERROR, paymentItem: [] },
    { name: successRows, count: 0, status: SUCCESS, paymentItem: [] },
  ]

  return itemsToShow.reduce((acc: GroupProp[], paymentItem: DraftPaymentItemLike) => {
    const user = getUserByItem(paymentItem)
    const paymentDisabled = disablePaymentItem(paymentItem, user)
    const paymentStatus = paymentDisabled ? ERROR : SUCCESS
    const data = acc.find((item) => item.status === paymentStatus)
    data!.count++
    data?.paymentItem.push(paymentItem)
    return acc
  }, defaultGroupResultConceptItems)
})

interface Props {
  checkable: boolean
  onSelectAll: (filteredPayments: PaymentItemLikeMinimal[]) => void
  selected: Set<number>
  selectable: Array<number | null>
  onSelectItem: (paymentItem: PaymentItemLikeMinimal) => void
  policyPeriodId: number
  paymentType: string | null | undefined
  filtersForm: FiltersForm
}

function getPaymentItemDisplayName(item: DraftPaymentItemLike): string {
  try {
    const user = getUserByItem(item)
    if (!user || !user.attributes) return ''
    const { first_name, last_name } = user.attributes
    return [first_name ?? '', last_name ?? '']
      .map((s) => String(s).trim())
      .filter(Boolean)
      .join(' ')
  } catch {
    return ''
  }
}

function filterPaymentItemsBySearch(
  items: DraftPaymentItemLike[],
  searchTerm: string
): DraftPaymentItemLike[] {
  if (!searchTerm.trim()) return items
  const term = searchTerm.toLowerCase().trim()
  return items.filter((item) => {
    const name = getPaymentItemDisplayName(item).toLowerCase()
    return name.includes(term)
  })
}

const PaymentsTable = ({
  checkable,
  onSelectAll,
  selected,
  selectable,
  onSelectItem,
  policyPeriodId,
  paymentType,
  filtersForm,
}: Props) => {
  const source = useDraftPaymentItemsDataCollectionSource(policyPeriodId, paymentType)
  const {
    data: collectionData,
    paginationInfo,
    setPage,
    isLoading: sourceLoading,
  } = useDataCollectionData(source)

  const { search, onSearch } = useDebouncedSearch()

  const records: DraftPaymentItemLike[] = collectionData?.records ?? []
  const filteredRecords = filterPaymentItemsBySearch(records, search)

  const groupPaymentItemsToShow = useGroupPaymentItems(filteredRecords).get()

  const pageBasedInfo =
    paginationInfo &&
    'type' in paginationInfo &&
    paginationInfo.type === 'pages' &&
    'currentPage' in paginationInfo &&
    'pagesCount' in paginationInfo
      ? paginationInfo
      : null

  const Options = () => (
    <Box
      width="full"
      alignItems="center"
      flexDirection="row"
      background="grey200"
      justifyContent="spaceBetween"
    >
      <SearchField
        variant="big"
        search={search}
        onSearch={onSearch}
        placeholder={i18n.t('common.search')}
      />
    </Box>
  )

  return (
    <>
      <Table autoLayout fullWidth options={Options}>
        <Table noRadius hideBorder>
          <Header
            checkable={checkable}
            selected={selected}
            selectable={selectable}
            filtersForm={filtersForm}
            onSelectAll={() => onSelectAll(filteredRecords)}
          />
        </Table>
        <Table noRadius hideBorder>
          {sourceLoading && records.length === 0 ? (
            <Box alignItems="center" padding="s24" background="white">
              <Spinner />
            </Box>
          ) : (
            <PaymentGroups
              groups={groupPaymentItemsToShow}
              checkable={checkable}
              onSelectItem={onSelectItem}
              selected={selected}
            />
          )}
        </Table>
      </Table>
      {pageBasedInfo !== null && (
        <Box padding="s16" background="grey200">
          <OnePagination
            totalPages={pageBasedInfo.pagesCount}
            currentPage={pageBasedInfo.currentPage}
            onPageChange={setPage}
            disabled={sourceLoading}
          />
        </Box>
      )}
    </>
  )
}

export default observer(PaymentsTable)
